/**
 * A2 合规风险合成的「纯逻辑核」 — 不依赖 webpack / fetch / JSON import，
 * 可被 frontend / Node demo / 测试 复用。
 *
 * 关系（与 complianceKnowledgeCore 同构）：
 * - synthesizeComplianceRisk.js     生产入口，import 静态 JSON + 调 fetch
 * - scripts/demo-a2-synthesis.mjs   Node demo，用 fs 读 JSON
 * - __tests__/                      jest 测试，mock fetch
 *
 * 保持本文件零外部依赖、纯函数 / 不可变。
 */

const REPO_WEB_BASE = 'https://github.com/1q2qq789/primary-lens/blob/main';

// ── Source link 转换 ──────────────────────────────────────────────────

/**
 * 把 topic body 内的相对路径转 GitHub web URL。
 *
 * 输入：
 *   relPath  '../../sources/hk/REF-Cap615-AMLO-2025-Aug.md'
 *   topicFile 'compliance/jurisdictions/hk/aml-travel-rule.md'
 * 输出：
 *   https://github.com/1q2qq789/primary-lens/blob/main/compliance/sources/hk/REF-Cap615-AMLO-2025-Aug.md
 */
export function relPathToWebUrl(relPath, topicFile) {
  if (!relPath || !topicFile) return relPath || '';
  if (/^https?:/.test(relPath)) return relPath;

  const topicDir = topicFile.split('/').slice(0, -1);
  const parts = relPath.split('/');

  let cwd = [...topicDir];
  for (const seg of parts) {
    if (seg === '..') cwd.pop();
    else if (seg === '.' || seg === '') continue;
    else cwd.push(seg);
  }
  return `${REPO_WEB_BASE}/${cwd.join('/')}`;
}

// ── Prompt builder ───────────────────────────────────────────────────

/**
 * 把 selected topics 的 body 拼装成 Claude prompt。
 * 强制要求 Claude 输出 JSON 结构 + 引用规则 + 项目 facts 注入。
 */
export function buildPrompt(projectFacts, selectedTopics) {
  const factsBlock = JSON.stringify(
    {
      name: projectFacts.name || 'Unknown',
      categories: projectFacts.categories || [],
      features: projectFacts.features || [],
      chains: projectFacts.chains || [],
      target_markets: projectFacts.target_markets || [],
      known_controls: projectFacts.known_controls || [],
      red_signals: projectFacts.red_signals || [],
      notes: projectFacts.notes || '',
    },
    null,
    2
  );

  const topicBlocks = selectedTopics
    .map((hit, i) => {
      const t = hit.topic;
      return `### Topic ${i + 1}: [${t.jurisdiction.toUpperCase()}] ${t.title}
- topic slug:           ${t.topic}
- severity_if_breached: ${t.severity_if_breached}
- match reason:         ${hit.matchReason}
- topic file:           compliance/jurisdictions/${t.jurisdiction}/${t.topic}.md

\`\`\`markdown
${t.body}
\`\`\``;
    })
    .join('\n\n---\n\n');

  return `你是 PrimaryLens 合规风险分析助手。基于以下 HK 监管法规摘要 + 项目特征，输出结构化合规 finding。

# 🔴 最重要的规则（**违反 = 输出无效**）

**你必须输出严格 JSON，不能有任何 JSON 之外的文字**。

- ❌ 不要 "好的"、"已收到"、"作为分析助手" 等开场白
- ❌ 不要 markdown 标题、表格、列表（如 "## 合规 Finding 报告"）
- ❌ 不要解释性段落或致谢
- ✅ 第一个字符必须是 \`{\`
- ✅ 最后一个字符必须是 \`}\`
- ✅ 输出整个回答只包含一个 JSON 对象

**Rule 0（防幻觉）**：每条 \`citation.section\` 引用前必须在下方 topic body 中实际出现。topic body 没出现的 section 编号不要发明。

已知幻觉（必须避免）：

- AML Guideline §12.11.25 / .26 / .27 — **§12.11 系列只到 12.11.24**
- SFC Staking Circular §X.X — 本 source 库尚未纳入原文，**不允许引用 Staking Circular 任何 section 编号**

# JSON 输出结构

\`\`\`json
{
  "overall_severity": "critical | high | medium | low",
  "summary": "1-2 句话总结合规维度风险（不超 80 字）",
  "summary_en": "English translation of summary, ~80 words",
  "findings": [
    {
      "severity": "critical | high | medium | low",
      "topic": "aml-travel-rule | vatp-licensing | staking | stablecoin",
      "requirement": "监管要求（1-2 行）",
      "requirement_en": "English translation of requirement",
      "gap": "项目缺口（1-2 行，结合 project facts 描述）",
      "gap_en": "English translation of gap",
      "citation": {
        "section": "如 'AML Guideline §12.11.5' 或 'Cap.615 附表 3B'",
        "source_link": "topic body 中已出现的相对路径，如 '../../sources/hk/REF-AML-Guideline-...md'"
      }
    }
  ]
}
\`\`\`

# 项目特征 (project facts)

\`\`\`json
${factsBlock}
\`\`\`

# 其他规则（**违反任何一条 = 输出无效**）

1. **citation.source_link** 必须是 topic body 中**已出现的相对路径**（以 \`../../sources/\` 开头），不要发明新路径。
2. **findings 数量**：3 – 5 条，优先 critical → high。**不要为了凑数生成 medium / low 假 finding**。
3. **gap 必须基于 project facts**：不要写"未提供信息"或"需要进一步核实"作为 gap；应基于 known_controls / red_signals / notes 推断具体缺口。
4. **不要照抄 topic body**：finding 的 requirement 是**摘要**，不是原文复制。
5. **summary_en / requirement_en / gap_en 必须提供**：不是可选字段，跟 zh 版本表达同样的意思（翻译，不是重新总结），供英文界面直接展示，不能省略或留空。

# 适用的监管 topic（含原文摘录）

${topicBlocks}

# 立即开始输出 JSON（第一个字符必须是 \`{\`）`;
}

/**
 * 用 Claude prefilling 强制 JSON 输出：在 messages 末尾加 assistant turn 以 "{" 开头，
 * Claude 必须从 "{" 继续，不能再写开场白。Anthropic API 标准技法。
 *
 * 返回 messages 数组直接喂给 /api/ai/score。调用方拿到 response 后需要在 raw text
 * 前面 prepend "{" 才能 parse —— parseAiResponseWithPrefill 处理了这一点。
 */
export function buildMessages(projectFacts, selectedTopics) {
  const prompt = buildPrompt(projectFacts, selectedTopics);
  return [
    { role: 'user', content: prompt },
    { role: 'assistant', content: '{' },
  ];
}

/**
 * 把 fetchFullProject 的 details + projectContext + defiData 投影成 selector 用的
 * projectFacts。逻辑独立可测，A3 时由 fetchFullProject 调用。
 */
export function factsFromProject({ details = {}, projectContext = null, defiData = null } = {}) {
  const tagSet = new Set();
  const addTagSource = src => {
    if (!src) return;
    if (Array.isArray(src)) src.forEach(t => t && tagSet.add(String(t)));
    else if (typeof src === 'string') src.split(/[,，;；]/).forEach(t => t.trim() && tagSet.add(t.trim()));
  };
  addTagSource(details.tags);
  addTagSource(details.industry_tags);
  addTagSource(details.tag);
  addTagSource(details.industry);

  const features = [];
  if (projectContext && Array.isArray(projectContext.productFeatures)) {
    for (const f of projectContext.productFeatures) {
      if (f && f.name) features.push(f.name);
    }
  }

  const chains = new Set();
  const addChain = c => c && typeof c === 'string' && chains.add(c.toLowerCase());
  if (Array.isArray(details.ecosystem)) details.ecosystem.forEach(addChain);
  if (details.chain) addChain(details.chain);
  if (projectContext && Array.isArray(projectContext.techStack)) projectContext.techStack.forEach(addChain);
  if (defiData && defiData.chains && Array.isArray(defiData.chains)) defiData.chains.forEach(addChain);

  const keywords = new Set();
  if (projectContext && Array.isArray(projectContext.evidenceTags)) {
    projectContext.evidenceTags.forEach(t => t && keywords.add(String(t)));
  }
  if (details.project_name) keywords.add(String(details.project_name).toLowerCase());

  const redSignals = [];
  if (projectContext && Array.isArray(projectContext.knownRisks)) {
    projectContext.knownRisks.forEach(r => r && redSignals.push(String(r)));
  }

  return {
    name: details.project_name || details.name || 'Unknown',
    categories: Array.from(tagSet),
    features,
    chains: Array.from(chains),
    target_markets: [],
    keywords: Array.from(keywords),
    known_controls: [],
    red_signals: redSignals,
    notes: projectContext ? String(projectContext.contextSummary || '').slice(0, 200) : '',
  };
}

/**
 * parseAiResponse 的 prefill 版本：处理 Claude assistant-prefill 场景。
 *
 * 策略：先按原样 parse（容纳 Claude / DeepSeek 没遵守 prefill 直接输出
 * "好的{...}" 的常见场景，parseAiResponse 的 regex 会自动定位 {...}）。
 * 如果直接 parse 失败，再尝试 prepend "{" 后 parse（处理 Claude 严格遵守
 * prefill、response 是 "  \"key\":..." 形式的延续输出）。
 *
 * 两个分支独立 try，选先成功的，避免之前 "trimmed.startsWith('{') 则不 prepend"
 * 误把 "好的{...}" 当连续输出 prepend 出 "{好的{...}" 双 { 的 parse 失败。
 */
export function parseAiResponseWithPrefill(txt) {
  if (typeof txt !== 'string') return { ok: false, error: 'response not string' };
  const direct = parseAiResponse(txt);
  if (direct.ok) return direct;
  const prepended = parseAiResponse('{' + txt);
  if (prepended.ok) return prepended;
  return direct;
}

// ── Response parser ──────────────────────────────────────────────────

/** 从 Claude response 文本中提取 JSON 对象，做常见容错处理 */
export function parseAiResponse(text) {
  if (typeof text !== 'string') {
    return { ok: false, error: 'response not string' };
  }
  const cleaned = text
    .replace(/```json\s*/g, '')
    .replace(/```\s*/g, '')
    .trim();
  const m = cleaned.match(/\{[\s\S]*\}/);
  if (!m) return { ok: false, error: 'no JSON object found' };
  try {
    const fixed = m[0]
      .replace(/[\x00-\x1f]/g, ' ')
      .replace(/,\s*([}\]])/g, '$1');
    const obj = JSON.parse(fixed);
    return { ok: true, data: obj };
  } catch (err) {
    return { ok: false, error: `JSON parse: ${err.message}` };
  }
}

// ── Finding validation ───────────────────────────────────────────────

const VALID_SEVERITY = new Set(['critical', 'high', 'medium', 'low']);

/**
 * 校验 + 后处理 finding：
 * - 必填字段、合法 severity
 * - citation.source_link 必须在拼成 prompt 时引入的 topic body 中出现
 * - 转换 source_link 相对路径 → GitHub web URL
 *
 * 不通过的 finding 会带 _invalid 标记，由调用方决定丢弃或保留供 debug。
 */
export function validateAndEnrichFindings(rawResult, selectedTopics) {
  if (!rawResult || typeof rawResult !== 'object') {
    return {
      overall_severity: 'low',
      summary: '',
      summary_en: '',
      findings: [],
      _invalid: 'result not object',
    };
  }

  const allowedLinks = new Set();
  const topicFileByLink = new Map();
  for (const hit of selectedTopics) {
    const t = hit.topic;
    const topicFile = `compliance/jurisdictions/${t.jurisdiction}/${t.topic}.md`;
    const linkMatches = t.body.match(/\.\.\/\.\.\/sources\/[^\s)\]"]+/g) || [];
    for (const link of linkMatches) {
      allowedLinks.add(link);
      topicFileByLink.set(link, topicFile);
    }
  }

  const findings = Array.isArray(rawResult.findings) ? rawResult.findings : [];
  const enriched = findings.map(f => {
    const issues = [];
    if (!VALID_SEVERITY.has(f.severity)) issues.push(`bad severity ${f.severity}`);
    if (!f.requirement) issues.push('missing requirement');
    if (!f.gap) issues.push('missing gap');
    if (!f.citation || !f.citation.section) issues.push('missing citation.section');
    const link = f.citation && f.citation.source_link;
    if (!link) {
      issues.push('missing citation.source_link');
    } else if (!allowedLinks.has(link)) {
      issues.push(`source_link not in any topic body: ${link}`);
    }

    const topicFile = link ? topicFileByLink.get(link) : null;
    const webUrl = link && topicFile ? relPathToWebUrl(link, topicFile) : null;

    return {
      ...f,
      citation: f.citation
        ? { ...f.citation, source_url: webUrl, _link_verified: allowedLinks.has(link) }
        : null,
      _invalid: issues.length ? issues : undefined,
    };
  });

  const validFindings = enriched.filter(f => !f._invalid);
  const invalidFindings = enriched.filter(f => f._invalid);

  return {
    overall_severity: VALID_SEVERITY.has(rawResult.overall_severity)
      ? rawResult.overall_severity
      : 'low',
    summary: typeof rawResult.summary === 'string' ? rawResult.summary : '',
    summary_en: typeof rawResult.summary_en === 'string' ? rawResult.summary_en : '',
    findings: validFindings,
    invalid_findings: invalidFindings,
    topics_considered: selectedTopics.map(h => ({
      jurisdiction: h.topic.jurisdiction,
      topic: h.topic.topic,
      severity_if_breached: h.topic.severity_if_breached,
      match_reason: h.matchReason,
    })),
  };
}
